// constants
import { CARD_OFFER_STATE } from "@payment-client/constants/Card";
import { API_SUCCESS_STATUS_CODE } from "@payment-client/constants/StatusCodes";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";

// helpers
import { captureError } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

// interfaces
import { ICardOffer, IRewardOfferNewCard } from "@swiggy-private/payment-ui";

// apis
import { checkOfferAndRewardOnNewCardAPI } from "@payment-client/apis/Card";
import { DOWNSTREAM_HOSTS } from "@payment-client/constants/Hosts";
import { getCardOfferPublic } from "@payment-client/apis/publicCard";

interface CheckOfferRewardProps {
    apiHost: string;
    cardBinNumber: string;
    selectedSplitPayMethods?: string;
    withSplitPay?: string;
    successCallback: (data: IRewardOfferNewCard | ICardOffer) => void;
    apiCallRef: {
        current: boolean;
    };
}

interface CheckOfferAndRewardOnCardProps {
    rewardsEnabled: boolean;
    rewardApplied?: boolean;
    cardBinNumber: string;
    selectedSplitPayMethods?: string;
    apiCallRef: React.MutableRefObject<boolean>;
    paymentPageOfferEnabled?: boolean;
    apiHost: string;
    withSplitPay: boolean;
    setCardOffer: (val: ICardOffer) => void;
}

export const checkOfferAndRewardOnCard = ({
    rewardsEnabled,
    rewardApplied,
    cardBinNumber,
    selectedSplitPayMethods,
    apiCallRef,
    paymentPageOfferEnabled,
    apiHost,
    withSplitPay,
    setCardOffer,
}: CheckOfferAndRewardOnCardProps) => {
    if (rewardsEnabled && rewardApplied) {
        checkOfferAndRewardOnNewCard({
            apiHost: DOWNSTREAM_HOSTS.PAYMENT_PRESENTATION_SERVICE_HOST,
            cardBinNumber,
            selectedSplitPayMethods,
            successCallback: _data => {
                const data = _data as IRewardOfferNewCard;
                setCardOffer({
                    couponApplicable: data?.coupon_applicable,
                    payableAmount: data?.payable_amount,
                    rewardApplicable: data?.reward_applicable,
                });
            },
            apiCallRef,
        });
    } else if (paymentPageOfferEnabled) {
        checkCardOffer({
            apiHost,
            cardBinNumber,
            withSplitPay: (!withSplitPay).toString(),
            successCallback: _data => {
                const data = _data as ICardOffer;
                setCardOffer({
                    offerAmount: data.offerAmount,
                    payableAmount: data.payableAmount,
                    discountTitle: data.discountTitle,
                });
            },
            apiCallRef,
        });
    }
};

export const checkOfferAndRewardOnNewCard = ({
    apiHost,
    cardBinNumber,
    selectedSplitPayMethods = "",
    successCallback,
    apiCallRef,
}: CheckOfferRewardProps) => {
    checkOfferAndRewardOnNewCardAPI(apiHost, cardBinNumber, selectedSplitPayMethods)
        .then(offer => {
            apiCallRef.current = false;
            if (isEmpty(offer) || isEmpty(offer.data) || offer.statusCode !== API_SUCCESS_STATUS_CODE) {
                return;
            }
            // nr event to check offer details
            recordNREvent({
                eventType: NEWRELIC_TABLES.CARD_OFFER_AND_REWARDS_TABLE,
                state: CARD_OFFER_STATE.OFFER_REWARDS_APPLY_SUCCESSFUL,
                cardBin: String(cardBinNumber),
                payableAmount: String(offer?.data?.payable_amount),
            });
            successCallback(offer.data);
        })
        .catch(err => {
            //nr event to check if no reward/offer was present or reward/offer fetch failed
            recordNREvent({
                eventType: NEWRELIC_TABLES.CARD_OFFER_AND_REWARDS_TABLE,
                state: CARD_OFFER_STATE.OFFER_REWARDS_NOT_AVAILABLE,
                cardBin: String(cardBinNumber),
            });
            captureError(err);
        });
};

export const checkCardOffer = ({
    apiHost,
    cardBinNumber,
    withSplitPay,
    successCallback,
    apiCallRef,
}: CheckOfferRewardProps) => {
    getCardOfferPublic(apiHost, cardBinNumber, withSplitPay || "false")
        .then(offer => {
            apiCallRef.current = false;
            if (isEmpty(offer)) {
                return;
            }
            //nr event to check offer details
            recordNREvent({
                eventType: NEWRELIC_TABLES.CARD_OFFER_TABLE,
                state: CARD_OFFER_STATE.OFFER_FETCH_SUCCESSFUL,
                cardBin: String(cardBinNumber),
                offerAmount: String(offer.offerAmount),
                payableAmount: String(offer.payableAmount),
                discountTitle: String(offer.discountTitle),
            });
            successCallback(offer);
        })
        .catch(err => {
            //nr event to check if no offer was present or offer fetch failed
            recordNREvent({
                eventType: NEWRELIC_TABLES.CARD_OFFER_TABLE,
                state: CARD_OFFER_STATE.OFFER_NOT_AVAILABLE,
                cardBin: String(cardBinNumber),
            });
            captureError(err);
        });
};
